import React, { useState } from 'react';
import { 
  obtenerCuadrantesPorPersona, 
  obtenerCuadrantesPorContrato 
} from '../services/ApiMarcajes';
import DisplayCuadrante from '../grid/DisplayCuadrante';
import { 
  Button, 
  TextField, 
  MenuItem, 
  CircularProgress,
  Alert,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  ButtonGroup
} from '@mui/material';
import { 
  Search as SearchIcon,
  Clear as ClearIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  DataObject as JsonIcon
} from '@mui/icons-material';
import './VisualizarCuadrantes.scss';

const VisualizarCuadrantes = () => {
  const [tipoConsulta, setTipoConsulta] = useState('persona'); // 'persona' o 'contrato'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cuadrantes, setCuadrantes] = useState(null);
  
  // Filtros para búsqueda por persona
  const [codPersona, setCodPersona] = useState('');
  const [empresaPersona, setEmpresaPersona] = useState('0001');
  const [delegacionPersona, setDelegacionPersona] = useState('');
  const [contratoPersona, setContratoPersona] = useState('');
  const [servicioPersona, setServicioPersona] = useState('');
  const [añoPersona, setAñoPersona] = useState(new Date().getFullYear().toString());
  const [mesPersona, setMesPersona] = useState('');
  
  // Filtros para búsqueda por contrato
  const [empresaContrato, setEmpresaContrato] = useState('0001');
  const [delegacionContrato, setDelegacionContrato] = useState('');
  const [contratoServicio, setContratoServicio] = useState('');
  const [servicioContrato, setServicioContrato] = useState('');
  const [añoContrato, setAñoContrato] = useState(new Date().getFullYear().toString());
  const [mesContrato, setMesContrato] = useState((new Date().getMonth() + 1).toString());

  const meses = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const años = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - 5 + i;
    return year.toString();
  });

  const handleBuscarPorPersona = async () => {
    try {
      setLoading(true);
      setError(null);
      setCuadrantes(null);

      if (!codPersona) {
        throw new Error('El código de persona es requerido');
      }

      console.log('🔍 Buscando cuadrantes por persona...');
      const response = await obtenerCuadrantesPorPersona(
        codPersona,
        empresaPersona || '0001',
        delegacionPersona || null,
        contratoPersona || null,
        servicioPersona || null,
        añoPersona || null,
        mesPersona || null
      );

      console.log('📦 Respuesta completa:', response);

      // Extraer items: API devuelve { errorCode, data: { total, items } }
      const items = response?.data?.data?.items ?? response?.data?.items;
      const cuadrantesData = Array.isArray(items)
        ? items
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : null;

      if (cuadrantesData && cuadrantesData.length > 0) {
        setCuadrantes(cuadrantesData);
        setError(null);
        console.log('✅ Cuadrantes obtenidos:', cuadrantesData.length);
      } else {
        setCuadrantes([]);
        setError('No se encontraron cuadrantes para esta persona');
      }
    } catch (err) {
      console.error('❌ Error al buscar cuadrantes:', err);
      setError(err.message || 'Error al buscar cuadrantes');
      setCuadrantes(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarPorContrato = async () => {
    try {
      setLoading(true);
      setError(null);
      setCuadrantes(null);

      if (!contratoServicio || !servicioContrato || !añoContrato || !mesContrato) {
        throw new Error('Contrato, servicio, año y mes son requeridos');
      }

      console.log('🔍 Buscando cuadrantes por contrato...');
      const response = await obtenerCuadrantesPorContrato(
        empresaContrato || '0001',
        delegacionContrato || null,
        contratoServicio,
        servicioContrato,
        añoContrato,
        mesContrato
      );

      console.log('📦 Respuesta completa:', response);

      // Extraer items: API devuelve { errorCode, data: { total, items } }
      const itemsContrato = response?.data?.data?.items ?? response?.data?.items;
      const cuadrantesDataContrato = Array.isArray(itemsContrato)
        ? itemsContrato
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : null;

      if (cuadrantesDataContrato && cuadrantesDataContrato.length > 0) {
        setCuadrantes(cuadrantesDataContrato);
        setError(null);
        console.log('✅ Cuadrantes obtenidos:', cuadrantesDataContrato.length);
      } else {
        setCuadrantes([]);
        setError('No se encontraron cuadrantes para este contrato/servicio');
      }
    } catch (err) {
      console.error('❌ Error al buscar cuadrantes:', err);
      setError(err.message || 'Error al buscar cuadrantes');
      setCuadrantes(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setCuadrantes(null);
    setError(null);
    if (tipoConsulta === 'persona') {
      setCodPersona('');
      setEmpresaPersona('0001');
      setDelegacionPersona('');
      setContratoPersona('');
      setServicioPersona('');
      setAñoPersona(new Date().getFullYear().toString());
      setMesPersona('');
    } else {
      setEmpresaContrato('0001');
      setDelegacionContrato('');
      setContratoServicio('');
      setServicioContrato('');
      setAñoContrato(new Date().getFullYear().toString());
      setMesContrato((new Date().getMonth() + 1).toString());
    }
  };

  // Función auxiliar para procesar datos igual que CuadranteDataGrid
  const procesarDatosParaExportacion = () => {
    if (!cuadrantes || cuadrantes.length === 0) return null;

    // Obtener año y mes, si no están especificados, usar el del primer cuadrante
    let año = parseInt(tipoConsulta === 'persona' ? añoPersona : añoContrato);
    let mes = parseInt(tipoConsulta === 'persona' ? mesPersona : mesContrato);
    
    if (!año || !mes) {
      // Obtener del primer cuadrante si no están especificados
      const primerCuadrante = cuadrantes[0];
      año = año || parseInt(primerCuadrante.CUADT_CAL || primerCuadrante.CUADD_CAL);
      mes = mes || parseInt(primerCuadrante.CUADT_MES || primerCuadrante.CUADD_MES);
    }
    
    if (!año || !mes) {
      alert('No se puede determinar el año y mes del cuadrante');
      return null;
    }
    
    const diasDelMes = new Date(año, mes, 0).getDate();

    if (tipoConsulta === 'contrato') {
      // BÚSQUEDA POR CONTRATO → Mostrar personas (vigilantes)
      const porPersona = {};
      
      cuadrantes.forEach(cuadrante => {
        const personaId = cuadrante.CUADT_PERSONA 
          ? `${cuadrante.CUADT_PERSONA}_${cuadrante.CUADD_TURNOPER}`
          : `Turno_${cuadrante.CUADD_TURNOPER}`;
        
        if (!porPersona[personaId]) {
          porPersona[personaId] = {
            persona: cuadrante.CUADT_PERSONA || personaId,
            nombre: cuadrante.VSSPER_NOM 
              ? `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2}`.trim()
              : `Turno ${cuadrante.CUADD_TURNOPER}`,
            turnosPorDia: {},
            totalHoras: 0,
            horasFestivas: 0,
            horasNocturnas: 0,
            numeroTurno: cuadrante.CUADD_TURNOPER,
            nif: cuadrante.VSSPER_NIF ? `${cuadrante.VSSPER_NIF}${cuadrante.VSSPER_NIF_DC || ''}` : null
          };
        }

        const dia = parseInt(cuadrante.CUADD_DD);
        if (dia && dia >= 1 && dia <= diasDelMes) {
          const horas = parseFloat(cuadrante.CUADD_HHTOTD) || 0;
          const horasNoct = parseFloat(cuadrante.CUADD_HHNOCD) || 0;
          
          porPersona[personaId].turnosPorDia[dia] = {
            turno: cuadrante.CUADD_TURNOPER,
            horas: horas,
            horaInicio: cuadrante.CUADD_HHINI,
            horaFin: cuadrante.CUADD_HHFIN,
            nocturno: horasNoct > 0
          };
          
          porPersona[personaId].totalHoras += horas;
          porPersona[personaId].horasNocturnas += horasNoct;
        }
      });

      return { 
        datos: Object.values(porPersona).sort((a, b) => a.nombre.localeCompare(b.nombre)), 
        diasDelMes,
        año,
        mes
      };
    } else {
      // BÚSQUEDA POR PERSONA → Mostrar contratos/servicios
      const porContrato = {};
      
      cuadrantes.forEach(cuadrante => {
        const contratoId = `${cuadrante.CUADD_CTRT}/${cuadrante.CUADD_SERV}`;
        
        if (!porContrato[contratoId]) {
          porContrato[contratoId] = {
            contratoServicio: contratoId,
            turnosPorDia: {},
            totalHoras: 0,
            horasFestivas: 0,
            horasNocturnas: 0
          };
        }

        const dia = parseInt(cuadrante.CUADD_DD);
        if (dia && dia >= 1 && dia <= diasDelMes) {
          const horas = parseFloat(cuadrante.CUADD_HHTOTD) || 0;
          const horasNoct = parseFloat(cuadrante.CUADD_HHNOCD) || 0;
          
          porContrato[contratoId].turnosPorDia[dia] = {
            turno: cuadrante.CUADD_TURNOPER,
            horas: horas,
            horaInicio: cuadrante.CUADD_HHINI,
            horaFin: cuadrante.CUADD_HHFIN,
            nocturno: horasNoct > 0
          };
          
          porContrato[contratoId].totalHoras += horas;
          porContrato[contratoId].horasNocturnas += horasNoct;
        }
      });

      return { 
        datos: Object.values(porContrato), 
        diasDelMes,
        año,
        mes
      };
    }
  };

  // Función para exportar a CSV
  const exportarACSV = () => {
    if (!cuadrantes || cuadrantes.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    try {
      console.log('📊 Exportando a CSV...');
      
      const datosProcessados = procesarDatosParaExportacion();
      if (!datosProcessados) {
        alert('Error procesando datos para exportación');
        return;
      }

      const { datos, diasDelMes, año, mes } = datosProcessados;
      
      // Obtener empresa y delegación del primer cuadrante
      const primerCuadrante = cuadrantes[0];
      const empresa = primerCuadrante.CUADT_EMP || primerCuadrante.CUADD_EMP || empresaContrato || empresaPersona || '-';
      const delegacion = primerCuadrante.CUADT_DELEG || primerCuadrante.CUADD_DELEG || delegacionContrato || delegacionPersona || '-';
      const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      
      // Crear encabezados
      let csvContent = 'data:text/csv;charset=utf-8,\uFEFF'; // BOM para UTF-8
      
      // Línea de información general
      csvContent += `Cuadrante de ${mesesNombres[mes - 1]} ${año}\n`;
      csvContent += `Empresa: ${empresa},Delegación: ${delegacion}\n`;
      if (tipoConsulta === 'contrato') {
        csvContent += `Contrato/Servicio: ${contratoServicio}/${servicioContrato}\n`;
      } else {
        csvContent += `Persona: ${codPersona}\n`;
      }
      csvContent += '\n'; // Línea en blanco
      
      // Primera fila de encabezados
      const encabezadoPrincipal = tipoConsulta === 'contrato' ? 'Vigilante' : 'Contrato/Servicio';
      csvContent += encabezadoPrincipal;
      
      // Columnas de días
      for (let dia = 1; dia <= diasDelMes; dia++) {
        csvContent += `,Día ${dia}`;
      }
      csvContent += ',Total H,H Noct\n';
      
      // Agregar datos
      datos.forEach(fila => {
        // Primera columna
        if (tipoConsulta === 'contrato') {
          const identificador = fila.nif ? `${fila.nombre} (NIF: ${fila.nif})` : `${fila.nombre} (T${fila.numeroTurno})`;
          csvContent += `"${identificador}"`;
        } else {
          csvContent += `"${fila.contratoServicio}"`;
        }
        
        // Columnas de días
        for (let dia = 1; dia <= diasDelMes; dia++) {
          const turnoData = fila.turnosPorDia[dia];
          if (turnoData) {
            csvContent += `,"${turnoData.horaInicio} (${turnoData.horas}h)"`;
          } else {
            csvContent += ',';
          }
        }
        
        // Totales
        csvContent += `,${fila.totalHoras.toFixed(2)},${fila.horasNocturnas.toFixed(2)}\n`;
      });

      // Crear y descargar archivo
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      const nombreArchivo = `Cuadrante_${empresa}_${delegacion}_${mesesNombres[mes - 1]}_${año}.csv`;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ CSV exportado correctamente');
    } catch (error) {
      console.error('❌ Error exportando CSV:', error);
      alert('Error al exportar CSV: ' + error.message);
    }
  };

  // Función para exportar a PDF
  const exportarAPDF = () => {
    if (!cuadrantes || cuadrantes.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    try {
      console.log('📄 Exportando a PDF...');
      
      const datosProcessados = procesarDatosParaExportacion();
      if (!datosProcessados) {
        alert('Error procesando datos para exportación');
        return;
      }

      const { datos, diasDelMes, año, mes } = datosProcessados;
      const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      
      // Obtener empresa y delegación del primer cuadrante
      const primerCuadrante = cuadrantes[0];
      const empresa = primerCuadrante.CUADT_EMP || primerCuadrante.CUADD_EMP || empresaContrato || empresaPersona || '-';
      const delegacion = primerCuadrante.CUADT_DELEG || primerCuadrante.CUADD_DELEG || delegacionContrato || delegacionPersona || '-';
      
      // Crear contenido HTML para el PDF
      let contenidoHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Cuadrante ${mesesNombres[mes - 1]} ${año}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            
            body { 
              font-family: Arial, sans-serif; 
              font-size: 11px;
              margin: 10px;
            }
            
            h1 { 
              text-align: center; 
              color: #1976d2; 
              font-size: 14px;
              margin-bottom: 6px;
              font-weight: bold;
            }
            
            .info { 
              text-align: center; 
              margin-bottom: 8px; 
              font-size: 11px;
              padding: 10px;
              background-color: #f8f9fa;
              border-radius: 4px;
              border: 1px solid #dee2e6;
              margin-top: 5px;
            }
            
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
              font-size: 9px;
              table-layout: fixed;
            }
            
            th { 
              background-color: #1976d2; 
              color: white; 
              padding: 6px 2px; 
              text-align: center;
              border: 1px solid #1565c0;
              font-size: 9px;
              font-weight: bold;
              white-space: nowrap;
            }
            
            th.nombre-col {
              text-align: left;
              width: 140px;
              padding: 6px 8px;
            }
            
            th.dia-col {
              width: 2.3%;
              min-width: 22px;
            }
            
            td { 
              border: 1px solid #ddd; 
              padding: 8px 2px;
              text-align: center;
              font-size: 8.5px;
              overflow: hidden;
              height: 45px;
              vertical-align: middle;
            }
            
            td.nombre-col {
              text-align: left;
              padding: 8px 8px;
              width: 140px;
              height: 45px;
            }
            
            .vigilante-nombre {
              font-weight: bold;
              font-size: 9.5px;
              margin-bottom: 2px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            
            .vigilante-id {
              font-size: 8px;
              color: #666;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            
            .turno-hora {
              font-weight: bold;
              display: block;
              font-size: 7px;
              margin-bottom: 2px;
              line-height: 1.2;
            }
            
            .turno-horas {
              font-size: 6.5px;
              color: #666;
              line-height: 1.2;
            }
            
            .total-col {
              background-color: #e3f2fd;
              font-weight: bold;
              font-size: 9px;
              padding: 6px 4px;
              width: 50px;
            }
            
            tr:nth-child(even) td { 
              background-color: #fafafa; 
            }
            
            .footer { 
              text-align: center; 
              margin-top: 12px; 
              font-size: 9px; 
              color: #666; 
              padding: 8px;
            }
            
            .fin-semana {
              background-color: #fff3cd !important;
            }
            
            @media print {
              body { 
                margin: 8mm;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              table { 
                page-break-inside: auto;
                width: 100%;
              }
              tr { 
                page-break-inside: avoid; 
                page-break-after: auto; 
              }
              thead { 
                display: table-header-group; 
              }
            }
          </style>
        </head>
        <body>
          <div class="info">
            <div style="margin-bottom: 5px; font-size: 12px;">
              <strong>${mesesNombres[mes - 1]} ${año}</strong> • <strong>Empresa:</strong> ${empresa} • <strong>Delegación:</strong> ${delegacion}
              ${tipoConsulta === 'contrato' ? ` • <strong>Contrato/Servicio:</strong> ${contratoServicio}/${servicioContrato}` : ` • <strong>Persona:</strong> ${codPersona}`}
            </div>
            <div style="font-size: 9px; color: #888;">
              Fecha de generación: ${new Date().toLocaleString('es-ES')}
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th class="nombre-col">${tipoConsulta === 'contrato' ? 'Vigilante' : 'Contrato/Servicio'}</th>
      `;
      
      // Encabezados de días
      for (let dia = 1; dia <= diasDelMes; dia++) {
        const fecha = new Date(año, mes - 1, dia);
        const diaSemana = fecha.getDay();
        const esFinDeSemana = diaSemana === 0 || diaSemana === 6;
        contenidoHTML += `<th class="dia-col ${esFinDeSemana ? 'fin-semana' : ''}">${dia}</th>`;
      }
      
      contenidoHTML += `
                <th class="total-col">Total H</th>
                <th class="total-col">H Noct</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      // Filas de datos
      datos.forEach(fila => {
        contenidoHTML += '<tr>';
        
        // Primera columna - Nombre
        if (tipoConsulta === 'contrato') {
          contenidoHTML += `
            <td class="nombre-col">
              <div class="vigilante-nombre">${fila.nombre}</div>
              <div class="vigilante-id">${fila.nif ? `NIF: ${fila.nif}` : `ID: ${fila.persona}`} • T${fila.numeroTurno}</div>
            </td>
          `;
        } else {
          contenidoHTML += `<td class="nombre-col">${fila.contratoServicio}</td>`;
        }
        
        // Columnas de días
        for (let dia = 1; dia <= diasDelMes; dia++) {
          const turnoData = fila.turnosPorDia[dia];
          const fecha = new Date(año, mes - 1, dia);
          const diaSemana = fecha.getDay();
          const esFinDeSemana = diaSemana === 0 || diaSemana === 6;
              
          
          if (turnoData) {
            contenidoHTML += `
              <td class="${esFinDeSemana ? 'fin-semana' : ''}">
                <span class="turno-hora">${turnoData.horaInicio}</span>
                <span class="turno-horas">${turnoData.horas}h</span>
              </td>
            `;
          } else {
            contenidoHTML += `<td class="${esFinDeSemana ? 'fin-semana' : ''}"></td>`;
          }
        }
        
        // Totales
        contenidoHTML += `
          <td class="total-col">${fila.totalHoras.toFixed(2)}h</td>
          <td class="total-col">${fila.horasNocturnas.toFixed(2)}h</td>
        `;
        
        contenidoHTML += '</tr>';
      });
      
      // Resumen
      const totalHoras = datos.reduce((sum, fila) => sum + fila.totalHoras, 0);
      const totalNocturnas = datos.reduce((sum, fila) => sum + fila.horasNocturnas, 0);
      
      contenidoHTML += `
            </tbody>
          </table>
          <div class="footer">
            <p><strong>Resumen:</strong> ${datos.length} ${tipoConsulta === 'contrato' ? 'vigilante(s)' : 'contrato(s)'} • Total Horas: ${totalHoras.toFixed(2)}h • Horas Nocturnas: ${totalNocturnas.toFixed(2)}h</p>
            <p style="font-size: 8px; margin-top: 5px;">Empresa: ${empresa} • Delegación: ${delegacion} • Generado desde el sistema de gestión de cuadrantes</p>
          </div>
        </body>
        </html>
      `;
      
      // Abrir ventana de impresión
      const ventanaImpresion = window.open('', '_blank');
      ventanaImpresion.document.write(contenidoHTML);
      ventanaImpresion.document.close();
      ventanaImpresion.print();
      
      console.log('✅ PDF preparado para impresión');
    } catch (error) {
      console.error('❌ Error exportando PDF:', error);
      alert('Error al exportar PDF: ' + error.message);
    }
  };

  // Función para exportar a JSON
  const exportarAJSON = () => {
    if (!cuadrantes || cuadrantes.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    try {
      console.log('📋 Exportando a JSON...');
      
      const datosProcessados = procesarDatosParaExportacion();
      if (!datosProcessados) {
        alert('Error procesando datos para exportación');
        return;
      }

      const { datos, diasDelMes, año, mes } = datosProcessados;
      
      // Obtener empresa y delegación del primer cuadrante
      const primerCuadrante = cuadrantes[0];
      const empresa = primerCuadrante.CUADT_EMP || primerCuadrante.CUADD_EMP || empresaContrato || empresaPersona || '-';
      const delegacion = primerCuadrante.CUADT_DELEG || primerCuadrante.CUADD_DELEG || delegacionContrato || delegacionPersona || '-';
      const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      
      // Crear objeto JSON con toda la información
      const jsonData = {
        metadata: {
          fechaGeneracion: new Date().toISOString(),
          periodo: {
            mes: mes,
            mesNombre: mesesNombres[mes - 1],
            año: año,
            diasDelMes: diasDelMes
          },
          empresa: empresa,
          delegacion: delegacion,
          tipoConsulta: tipoConsulta,
          ...(tipoConsulta === 'contrato' ? {
            contrato: contratoServicio,
            servicio: servicioContrato
          } : {
            codigoPersona: codPersona
          })
        },
        resumen: {
          totalRegistros: datos.length,
          totalHoras: datos.reduce((sum, fila) => sum + fila.totalHoras, 0),
          totalHorasNocturnas: datos.reduce((sum, fila) => sum + fila.horasNocturnas, 0),
          totalHorasFestivas: datos.reduce((sum, fila) => sum + fila.horasFestivas, 0)
        },
        datos: datos,
        datosOriginales: cuadrantes
      };

      // Convertir a JSON con formato legible
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // Crear blob y descargar
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nombreArchivo = `Cuadrante_${empresa}_${delegacion}_${mesesNombres[mes - 1]}_${año}.json`;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('✅ JSON exportado correctamente');
    } catch (error) {
      console.error('❌ Error exportando JSON:', error);
      alert('Error al exportar JSON: ' + error.message);
    }
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '24px' }}>
        <Tabs 
          value={tipoConsulta} 
          onChange={(e, newValue) => {
            setTipoConsulta(newValue);
            handleLimpiar();
          }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px'
            }
          }}
        >
          <Tab label="👤 Por Persona" value="persona" />
          <Tab label="📄 Por Contrato/Servicio" value="contrato" />
        </Tabs>
      </Box>

      {/* Form Container */}
      <Paper sx={{ padding: '20px', marginBottom: '24px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        {tipoConsulta === 'persona' ? (
          /* Formulario Por Persona */
          <Box>
            <Typography variant="h6" sx={{ marginBottom: '16px', fontWeight: 500, color: '#495057' }}>
              💡 Hoja de Trabajo Personal
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: '20px', color: '#6c757d' }}>
              Consulta los cuadrantes de una persona específica. Puedes filtrar por contrato, servicio, año y mes.
            </Typography>

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <TextField
                label="Código de Persona *"
                value={codPersona}
                onChange={(e) => setCodPersona(e.target.value)}
                placeholder="Ej: 12189"
                disabled={loading}
                size="small"
                sx={{ minWidth: '180px', flex: '1 1 auto' }}
                required
              />

              <TextField
                label="Empresa"
                value={empresaPersona}
                onChange={(e) => setEmpresaPersona(e.target.value)}
                placeholder="0001"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              />

              <TextField
                label="Delegación"
                value={delegacionPersona}
                onChange={(e) => setDelegacionPersona(e.target.value)}
                placeholder="Ej: 06"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              />

              <TextField
                label="Contrato"
                value={contratoPersona}
                onChange={(e) => setContratoPersona(e.target.value)}
                placeholder="Ej: 11384"
                disabled={loading}
                size="small"
                sx={{ minWidth: '140px' }}
              />

              <TextField
                label="Servicio"
                value={servicioPersona}
                onChange={(e) => setServicioPersona(e.target.value)}
                placeholder="Ej: 01"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              />

              <TextField
                select
                label="Año"
                value={añoPersona}
                onChange={(e) => setAñoPersona(e.target.value)}
                disabled={loading}
                size="small"
                sx={{ minWidth: '100px' }}
              >
                <MenuItem value="">Todos</MenuItem>
                {años.map(año => (
                  <MenuItem key={año} value={año}>{año}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Mes"
                value={mesPersona}
                onChange={(e) => setMesPersona(e.target.value)}
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              >
                <MenuItem value="">Todos</MenuItem>
                {meses.map(mes => (
                  <MenuItem key={mes.value} value={mes.value}>{mes.label}</MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                onClick={handleBuscarPorPersona}
                disabled={loading || !codPersona}
                startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ minWidth: '140px', height: '40px' }}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleLimpiar}
                disabled={loading}
                startIcon={<ClearIcon />}
                sx={{ minWidth: '120px', height: '40px' }}
              >
                Limpiar
              </Button>
            </Box>
          </Box>
        ) : (
          /* Formulario Por Contrato */
          <Box>
            <Typography variant="h6" sx={{ marginBottom: '16px', fontWeight: 500, color: '#495057' }}>
              💡 Cuadrante por Contrato/Servicio
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: '20px', color: '#6c757d' }}>
              Consulta el cuadrante completo de un contrato/servicio para un mes específico.
            </Typography>

            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <TextField
                label="Empresa"
                value={empresaContrato}
                onChange={(e) => setEmpresaContrato(e.target.value)}
                placeholder="0001"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              />

              <TextField
                label="Delegación"
                value={delegacionContrato}
                onChange={(e) => setDelegacionContrato(e.target.value)}
                placeholder="Ej: 06"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
              />

              <TextField
                label="Contrato *"
                value={contratoServicio}
                onChange={(e) => setContratoServicio(e.target.value)}
                placeholder="Ej: 11384"
                disabled={loading}
                size="small"
                sx={{ minWidth: '140px' }}
                required
              />

              <TextField
                label="Servicio *"
                value={servicioContrato}
                onChange={(e) => setServicioContrato(e.target.value)}
                placeholder="Ej: 01"
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
                required
              />

              <TextField
                select
                label="Año *"
                value={añoContrato}
                onChange={(e) => setAñoContrato(e.target.value)}
                disabled={loading}
                size="small"
                sx={{ minWidth: '100px' }}
                required
              >
                {años.map(año => (
                  <MenuItem key={año} value={año}>{año}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Mes *"
                value={mesContrato}
                onChange={(e) => setMesContrato(e.target.value)}
                disabled={loading}
                size="small"
                sx={{ minWidth: '120px' }}
                required
              >
                {meses.map(mes => (
                  <MenuItem key={mes.value} value={mes.value}>{mes.label}</MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                onClick={handleBuscarPorContrato}
                disabled={loading || !contratoServicio || !servicioContrato || !añoContrato || !mesContrato}
                startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ minWidth: '140px', height: '40px' }}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleLimpiar}
                disabled={loading}
                startIcon={<ClearIcon />}
                sx={{ minWidth: '120px', height: '40px' }}
              >
                Limpiar
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            ❌ Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      )}

      {/* Success Message & Export Buttons */}
      {cuadrantes && cuadrantes.length > 0 && (
        <Box sx={{ marginBottom: '24px' }}>
          <Alert severity="success" sx={{ marginBottom: '16px' }}>
            ✅ Se encontraron <strong>{cuadrantes.length}</strong> registro(s) de cuadrante
          </Alert>

          {/* Export Buttons */}
          <Paper sx={{ padding: '16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '12px', fontWeight: 600, color: '#495057' }}>
              📥 Exportar Datos
            </Typography>
            <ButtonGroup variant="contained" sx={{ gap: '8px' }}>
              <Button
                onClick={exportarAPDF}
                startIcon={<PdfIcon />}
                sx={{
                  backgroundColor: '#dc3545',
                  '&:hover': { backgroundColor: '#c82333' },
                  textTransform: 'none'
                }}
              >
                Exportar a PDF
              </Button>
              <Button
                onClick={exportarACSV}
                startIcon={<CsvIcon />}
                sx={{
                  backgroundColor: '#28a745',
                  '&:hover': { backgroundColor: '#218838' },
                  textTransform: 'none'
                }}
              >
                Exportar a CSV
              </Button>
              <Button
                onClick={exportarAJSON}
                startIcon={<JsonIcon />}
                sx={{
                  backgroundColor: '#007bff',
                  '&:hover': { backgroundColor: '#0056b3' },
                  textTransform: 'none'
                }}
              >
                Exportar a JSON
              </Button>
            </ButtonGroup>
          </Paper>
        </Box>
      )}

      {/* Display Results */}
      {cuadrantes && (
        <DisplayCuadrante 
          cuadrantes={cuadrantes} 
          tipoConsulta={tipoConsulta}
          año={tipoConsulta === 'persona' ? añoPersona : añoContrato}
          mes={tipoConsulta === 'persona' ? mesPersona : mesContrato}
        />
      )}
    </Box>
  );
};

export default VisualizarCuadrantes;

